import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { BASE_API } from "@/constants";
import { useFetchData } from "@/hooks";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
    // Initialize from localStorage for instant load (Sat Set UX)
    const [user, setUser] = useState(() => {
        try {
            const saved = localStorage.getItem("user_data");
            return saved ? JSON.parse(saved) : null;
        } catch (e) {
            return null;
        }
    });

    const isLoggedIn = localStorage.getItem("is_logged_in");

    // Background fetch to validate session & update data
    const {
        data: userData,
        isLoading,
        error,
        refetch: fetchUser,
    } = useFetchData(isLoggedIn ? "user" : null);

    // Sync remote data to state & storage
    useEffect(() => {
        if (userData) {
            const updatedUser = {
                ...userData,
                avatar: userData.profile_photo_url || null,
            };
            setUser(updatedUser);
            localStorage.setItem("user_data", JSON.stringify(updatedUser));
            Sentry.setUser({ id: updatedUser.id, email: updatedUser.email, username: updatedUser.name });
        } else if (!isLoggedIn) {
            setUser(null);
            localStorage.removeItem("user_data");
            Sentry.setUser(null);
        }
    }, [userData, isLoggedIn]);

    // Handle 401
    useEffect(() => {
        if (error?.response?.status === 401) {
            setUser(null);
            localStorage.removeItem("is_logged_in");
            localStorage.removeItem("user_data");
        }
    }, [error]);

    const logout = async () => {
        try {
            await axios.post(`${BASE_API}/logout`);
        } catch (error) {
            console.error("Logout failed", error);
        }
        setUser(null);
        Sentry.setUser(null);
        localStorage.removeItem("is_logged_in");
        localStorage.removeItem("user_data");
        window.location.href = "/login"; // Hard redirect to clear any state
    };

    const loginSuccess = (userData) => {
        const updatedUser = {
            ...userData,
            avatar: userData.profile_photo_url || null,
        };
        setUser(updatedUser);
        localStorage.setItem("user_data", JSON.stringify(updatedUser));
        localStorage.setItem("is_logged_in", "true");
    };

    const hasPermission = (permission) =>
        !permission || user?.permissions?.includes(permission);

    return (
        <UserContext.Provider
            value={{
                user,
                setUser,
                isLoading,
                fetchUser,
                logout,
                loginSuccess,
                hasPermission,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
